// Workflow chains in words, pure (chainModel.selftest.ts): a schedule whose
// When is `after` runs a workflow once a run of ANOTHER workflow ends
// (services/schedules/chain.py): "completed" = it succeeded, and
// "completed_or_failures" = it completed even with failed steps. Only the
// operator makes one; only a workflow is run that way. A chain fires nothing
// for runs that ended before it was made, once for the latest of several
// runs that ended together, at most CHAIN_FIRES_PER_DAY times a day, and a
// chain of chains is at most CHAIN_DEPTH long (the server refuses a loop).
//
// A text input of the chained workflow may carry {{upstream.run_id}} or
// {{upstream.workflow}}: the run that set it off, filled in at the start.

import type { UnifiedSchedule } from "@/api/schedulesUnified";
import { humanizeSlug, pluralize } from "@/lib/format";

export type AfterOn = "completed" | "completed_or_failures";
export type After = { workflow: string; on: AfterOn };

/** The server's own limits (the list's `limits` says them too). */
export const CHAIN_DEPTH = 4;
export const CHAIN_FIRES_PER_DAY = 24;

/** What a text input may carry: the run that set this one off. */
export const UPSTREAM_PLACEHOLDERS: readonly { token: string; means: string }[] = [
  { token: "{{upstream.run_id}}", means: "the id of the run that set it off" },
  { token: "{{upstream.workflow}}", means: "that run's workflow (its web address)" },
];

type NameOf = (slug: string) => string;
const plainName: NameOf = (slug) => humanizeSlug(slug) || slug;

/** "After Morning analysis succeeds", "After Morning analysis completes (failed steps too)". */
export function afterWords(after: After, nameOf: NameOf = plainName, capital = true): string {
  const name = nameOf(after.workflow) || plainName(after.workflow);
  const words = after.on === "completed_or_failures" ? `after ${name} completes (failed steps too)` : `after ${name} succeeds`;
  return capital ? words.charAt(0).toUpperCase() + words.slice(1) : words;
}

/** A row's chain, read safely ({} rows, a row without one: null). */
export function afterOf(row: Pick<UnifiedSchedule, "when">): After | null {
  const a = row.when.after;
  if (!a || !a.workflow) return null;
  return { workflow: a.workflow, on: a.on === "completed_or_failures" ? "completed_or_failures" : "completed" };
}

export interface WorkflowChains<T> {
  /** The schedules that run THIS workflow after another one. */
  runsAfter: T[];
  /** The schedules that run another workflow after this one. */
  setsOff: T[];
}

/** A workflow's chains, both ways (by its web address). */
export function chainsOf<T extends Pick<UnifiedSchedule, "when" | "who" | "name">>(rows: readonly T[], slug: string): WorkflowChains<T> {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  const chained = rows.filter((r) => !!afterOf(r));
  return {
    runsAfter: chained.filter((r) => r.who.kind === "workflow" && r.who.ref === slug).sort(byName),
    setsOff: chained.filter((r) => afterOf(r)?.workflow === slug).sort(byName),
  };
}

/**
 * The chain's state in one quiet line: how often it ran today against the
 * limit, a run it waits to follow, earlier runs it passed over. "" when
 * there is nothing to say (it never ran).
 */
export function chainStateLine(chain: UnifiedSchedule["chain"] | null | undefined, limit = CHAIN_FIRES_PER_DAY): string {
  if (!chain) return "";
  const parts: string[] = [];
  if (chain.waiting_for_run_id) parts.push("Waiting to follow a run that ended while the last one was still going");
  if (chain.fires_today > 0) parts.push(chain.fires_today >= limit ? `Ran ${limit} times today: no more until tomorrow` : `Ran ${pluralize(chain.fires_today, "time")} today`);
  if (chain.passed_over > 0) parts.push(`followed only the latest of ${chain.passed_over + 1} runs that ended together`);
  const line = parts.join("; ");
  return line ? line.charAt(0).toUpperCase() + line.slice(1) : "";
}

/** Placeholders said in words, for a schedule's details ("{{upstream.run_id}}" → "the run that set it off"). */
export function placeholderWords(text: string): string {
  return text.replace(/\{\{\s*upstream\.run_id\s*\}\}/g, "the run that set it off").replace(/\{\{\s*upstream\.workflow\s*\}\}/g, "its workflow");
}

/** The refusals the server gives for a chain, in words (null: not a chain refusal). */
export function chainErrorText(code: string | null | undefined, detail: Record<string, unknown> = {}): string | null {
  switch (code) {
    case "chain_self":
      return "A workflow cannot run after itself.";
    case "chain_exists":
      return "There is already a schedule that runs this workflow after that one: change that one instead.";
    case "chain_cycle": {
      const loop = Array.isArray(detail.loop) ? detail.loop.map((s) => plainName(String(s))).join(" → ") : "";
      return `That would make a loop${loop ? ` (${loop})` : ""}: each would keep setting the next one off.`;
    }
    case "chain_too_deep": {
      const limit = typeof detail.limit === "number" ? detail.limit : CHAIN_DEPTH;
      return `At most ${limit} workflows can follow one another in a row.`;
    }
    case "unknown_workflow":
      return "That workflow does not exist (any more).";
    default:
      return null;
  }
}

/** The editor's closed line: "After Morning analysis succeeds", "… or 2 others" ("" for none). */
export function chainSummary(runsAfter: readonly Pick<UnifiedSchedule, "when" | "enabled">[], nameOf: NameOf = plainName): string {
  const on = runsAfter.filter((r) => r.enabled);
  const first = on.map(afterOf).find((a): a is After => !!a);
  if (!first) return "";
  const more = on.length - 1;
  return `${afterWords(first, nameOf)}${more > 0 ? ` or ${pluralize(more, "other")}` : ""}`;
}
